import React from 'react';
import { Search, RefreshCw, SearchX, ChevronUp, ChevronDown } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './Table';

export interface ColumnDef<T> {
  header: string;
  key: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  width?: string;
  sortable?: boolean;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  isError?: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  searchTerm?: string;
  onSearchChange?: (search: string) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  errorMessage?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (key: string) => void;
  theme?: 'default' | 'admin';
  onRowClick?: (item: T, index: number) => void;
}

export function DataTable<T extends { id: string | number }>({
  data,
  columns,
  isLoading = false,
  isError = false,
  total,
  page,
  limit,
  onPageChange,
  searchTerm = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No entries found',
  errorMessage = 'Failed to retrieve database records. Please try again.',
  sortBy,
  sortOrder,
  onSortChange,
  theme = 'default',
  onRowClick,
}: DataTableProps<T>) {
  return (
    <div className="flex flex-col">
      {/* Top Search Controls Bar */}
      {onSearchChange && (
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4" style={{ marginBottom: '32px' }}>
          <div className="relative flex-1 max-w-md">
            <span className="absolute text-slate-400" style={{ left: '16px', top: '50%', transform: 'translateY(-50%)' }}>
              <Search size={18} />
            </span>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => {
                onSearchChange(e.target.value);
                onPageChange(1); // Reset page to 1 on new search
              }}
              style={{ 
                padding: '14px 16px 14px 44px',
                background: 'var(--admin-card-bg, #f8fafc)',
                color: 'var(--admin-card-text, #0f172a)',
                borderColor: 'var(--admin-card-border, #e2e8f0)'
              }}
              className="w-full rounded-xl border text-sm outline-none transition-all duration-200 focus:border-[#0ea5e9] focus:ring-4 focus:ring-[#0ea5e9]/10 shadow-sm"
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      {isLoading ? (
        <div 
          style={{
            backgroundColor: 'var(--admin-card-bg, #ffffff)',
            borderColor: 'var(--admin-card-border, #e2e8f0)',
            borderRadius: theme === 'admin' ? '0' : undefined
          }}
          className={theme === 'admin' ? "w-full overflow-x-auto border" : "w-full overflow-hidden border rounded-2xl shadow-sm my-6"}
        >
          <table className="w-full border-collapse text-left text-sm">
            <thead style={{ 
              backgroundColor: theme === 'admin' ? 'var(--admin-table-header-bg, rgba(255, 255, 255, 0.02))' : 'var(--admin-table-header-bg, #f8fafc)', 
              borderBottom: '1px solid var(--admin-card-border, #e2e8f0)' 
            }}>
              <tr>
                {columns.map((col, idx) => (
                  <th 
                    key={`skel-h-${idx}`} 
                    style={{ 
                      padding: theme === 'admin' ? '12px 16px' : '18px 24px', 
                      width: col.width, 
                      color: theme === 'admin' ? 'var(--admin-card-subtext, #475569)' : 'var(--admin-card-text, #475569)',
                      opacity: theme === 'admin' ? undefined : 0.8,
                      fontSize: theme === 'admin' ? '10px' : undefined,
                      letterSpacing: theme === 'admin' ? '0.05em' : undefined
                    }} 
                    className={theme === 'admin' ? "font-bold uppercase tracking-wider" : "text-[11px] font-bold uppercase tracking-wider"}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: limit }).map((_, rowIdx) => (
                <tr 
                  key={`skel-r-${rowIdx}`} 
                  style={{ borderBottom: '1px solid var(--admin-card-border, #f1f5f9)' }} 
                  className={theme === 'admin' ? "" : "animate-pulse"}
                >
                  {columns.map((col, colIdx) => (
                    <td key={`skel-d-${rowIdx}-${colIdx}`} style={{ padding: theme === 'admin' ? '14px 16px' : '16px 24px' }}>
                      <div 
                        className={theme === 'admin' ? "admin-skeleton h-4 w-3/4" : "h-4 rounded w-3/4"} 
                        style={theme === 'admin' ? { borderRadius: '0' } : { backgroundColor: 'var(--admin-card-border, #e2e8f0)', opacity: 0.3 }}
                      ></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : isError ? (
        <div 
          style={{
            backgroundColor: 'var(--admin-card-bg, #ffffff)',
            borderColor: 'var(--admin-card-border, #e2e8f0)'
          }}
          className="h-64 flex flex-col items-center justify-center gap-3 text-center border rounded-xl shadow-sm px-4"
        >
          <span className="text-amber-600 text-lg font-bold">⚠️ DATABASE ACCESS DENIED</span>
          <p className="text-xs font-bold text-slate-400 max-w-md">{errorMessage}</p>
        </div>
      ) : data.length === 0 ? (
        <div 
          style={{
            backgroundColor: 'var(--admin-card-bg, #ffffff)',
            borderColor: 'var(--admin-card-border, #e2e8f0)',
            color: 'var(--admin-card-text, #0f172a)'
          }}
          className="h-64 flex flex-col items-center justify-center gap-3 text-center border rounded-xl shadow-sm px-4"
        >
          <SearchX className="text-slate-400" size={48} />
          <p className="text-sm font-bold">{emptyMessage}</p>
          <p className="text-xs font-bold text-slate-400">Try adjusting your search query.</p>
        </div>
      ) : (
        <>
          {/* Reusable Styled Table */}
          <Table theme={theme}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {columns.map((col, index) => (
                  <TableHead
                    key={col.key || index}
                    className={`${col.headerClassName || ''} ${col.sortable && onSortChange ? 'group cursor-pointer select-none hover:bg-slate-800 transition-colors' : ''}`}
                    style={{ width: col.width }}
                    onClick={() => {
                      if (col.sortable && onSortChange) {
                        onSortChange(col.key);
                      }
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      {col.header}
                      {col.sortable && sortBy === col.key && (
                        <span className="text-teal-400">
                          {sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </span>
                      )}
                      {col.sortable && sortBy !== col.key && (
                        <span className="text-slate-600 opacity-0 group-hover:opacity-100">
                          <ChevronUp size={14} />
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((item, rowIdx) => (
                <TableRow 
                  key={item.id} 
                  onClick={onRowClick ? () => onRowClick(item, rowIdx) : undefined}
                >
                  {columns.map((col, colIdx) => (
                    <TableCell
                      key={`${item.id}-${col.key || colIdx}`}
                      className={`${col.className || ''}`}
                    >
                      {col.render ? col.render(item, rowIdx) : (item as any)[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}
